import DOMPurify from "dompurify";

/**
 * Sanitizes untrusted email HTML before it is inserted into the reading pane.
 *
 * Defence in depth:
 *  1. `USE_PROFILES.html` restricts output to the HTML profile (no SVG/MathML).
 *  2. `FORBID_ATTR` drops `style` and `class`, preventing CSS exfiltration and
 *     layout-based phishing.
 *  3. DOMPurify strips `javascript:` URIs and every `on*` handler by default.
 *  4. Links are post-processed to open in a new tab with `rel="noopener noreferrer
 *     nofollow"`.
 *
 * The result is memoized by the caller (`useMemo`) because sanitization is
 * O(size of HTML).
 */
export function sanitizeEmailHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_ATTR: ["style", "class"],
    ADD_ATTR: ["target"],
  });
  return hardenLinks(clean);
}

/** Forces every anchor to open safely in a new context. */
export function hardenLinks(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const anchor of Array.from(doc.querySelectorAll("a"))) {
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer nofollow");
  }
  return doc.body.innerHTML;
}
